import { useEffect, useState } from "react";
import axios from "axios";
import { X, Download, Loader2, FileWarning } from "lucide-react";

export default function DocumentPreviewModal({ url, filename = "document", contentType, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [error, setError] = useState("");
  const [type, setType] = useState(contentType || "");

  useEffect(() => {
    let cancelled = false;
    let createdUrl = "";
    (async () => {
      try {
        const res = await axios.get(url, { withCredentials: true, responseType: "blob" });
        if (cancelled) return;
        const ct = res.headers["content-type"] || contentType || "";
        setType(ct);
        const blob = new Blob([res.data], { type: ct || "application/octet-stream" });
        createdUrl = window.URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError(err.response?.statusText || err.message || "Failed to load preview");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [url, contentType]);

  const isImage = (type || "").startsWith("image/") || /\.(png|jpe?g|webp|heic|heif|gif|svg)$/i.test(filename);
  const isPdf = (type || "").includes("pdf") || /\.pdf$/i.test(filename);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="doc-preview-modal">
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1E3050]">
          <span className="text-sm text-white truncate flex-1 mr-4 font-mono">{filename}</span>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a href={blobUrl} download={filename} data-testid="doc-preview-download"
                className="inline-flex items-center gap-1.5 bg-[#1E3050] text-white px-3 py-1.5 rounded-sm text-xs hover:bg-[#1E3050]/70">
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <button onClick={onClose} data-testid="doc-preview-close" className="p-1.5 text-[#857973] hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#0A1628] flex items-center justify-center">
          {!blobUrl && !error && (
            <div className="flex flex-col items-center gap-2 text-[#857973]">
              <Loader2 className="w-6 h-6 animate-spin text-[#F59073]" />
              <span className="text-xs font-mono">LOADING…</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center gap-2 text-red-400 p-8 text-center">
              <FileWarning className="w-8 h-8" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {blobUrl && isImage && (
            <img src={blobUrl} alt={filename} className="max-w-full max-h-full object-contain" data-testid="doc-preview-image" />
          )}
          {blobUrl && isPdf && (
            <iframe src={blobUrl} title={filename} className="w-full h-full bg-white" data-testid="doc-preview-iframe" />
          )}
          {blobUrl && !isImage && !isPdf && (
            <div className="flex flex-col items-center gap-2 text-[#857973] p-8 text-center">
              <FileWarning className="w-8 h-8 text-[#F59073]" />
              <span className="text-sm">Preview not supported for this file type.</span>
              <a href={blobUrl} download={filename} className="text-[#F59073] underline text-xs">Download instead</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
